<template>
  <div>
    <Communication-add
      :is-active="isModalActive"
      :customer-id="Number(this.$route.params.id)"
      :is-add="isAdd"
      :is-edit="isEdit"
      :action="action"
      :edit-object-name="editObjectName"
      @confirm="editConfirm"
      @cancel="editCancel"
    />

    <div>
      <b-table
        :data="filteredTableData"
        :hoverable="true"
        :mobile-cards="false"
        detailed
        detail-key="id"
        :show-detail-icon="true"
      >
        <b-table-column field="date" label="Date" width="120">
          <template #default="props">
            {{ props.row.date }}
          </template>
          <template #header>
            <div class="header-cell">
              <span>Date</span>
              <a
                v-show="isSorted('date')"
                href="#"
                @click.prevent="setSortField('date')"
                class="sort-link"
                :class="{ active: isSorted('date') }"
              >
                <b-icon :icon="getSortIcon('date')" size="is-small"></b-icon>
              </a>
            </div>
            <b-input
              v-model="searchFilters.date"
              type="text"
              placeholder="Search"
              size="is-small"
              icon="search"
            ></b-input>
          </template>
        </b-table-column>

        <b-table-column field="type" label="Type" width="120">
          <template #default="props">
            {{ props.row.type }}
          </template>
          <template #header>
            <div class="header-cell">
              <span>Type</span>
              <a
                v-show="isSorted('type')"
                href="#"
                @click.prevent="setSortField('type')"
                class="sort-link"
                :class="{ active: isSorted('type') }"
              >
                <b-icon :icon="getSortIcon('type')" size="is-small"></b-icon>
              </a>
            </div>
            <b-input
              v-model="searchFilters.type"
              type="text"
              placeholder="Search"
              size="is-small"
              icon="search"
            ></b-input>
          </template>
        </b-table-column>

        <b-table-column field="title" label="Title">
          <template #default="props">
            {{ props.row.title }}
          </template>
          <template #header>
            <div class="header-cell">
              <span>Title</span>
              <a
                v-show="isSorted('title')"
                href="#"
                @click.prevent="setSortField('title')"
                class="sort-link"
                :class="{ active: isSorted('title') }"
              >
                <b-icon :icon="getSortIcon('title')" size="is-small"></b-icon>
              </a>
            </div>
            <b-input
              v-model="searchFilters.title"
              type="text"
              placeholder="Search"
              size="is-small"
              icon="search"
            ></b-input>
          </template>
        </b-table-column>

        <b-table-column field="receiver" label="Receiver" width="200">
          <template #default="props">
            <span v-if="props.row.cc && props.row.cc.length">
              {{
                Array.isArray(props.row.cc)
                  ? props.row.cc.join(", ")
                  : props.row.cc
              }}
            </span>
            <span v-else>-</span>
          </template>
          <template #header>
            <div class="header-cell">
              <span>Receiver</span>
              <a
                v-show="isSorted('receiver')"
                href="#"
                @click.prevent="setSortField('receiver')"
                class="sort-link"
                :class="{ active: isSorted('receiver') }"
              >
                <b-icon
                  :icon="getSortIcon('receiver')"
                  size="is-small"
                ></b-icon>
              </a>
            </div>
            <b-input
              v-model="searchFilters.receiver"
              type="text"
              placeholder="Search"
              size="is-small"
              icon="search"
            ></b-input>
          </template>
        </b-table-column>
        <b-table-column field="actions" label="" width="20"> </b-table-column>
        <b-table-column custom-key="empty-col" label="" width="20">
        </b-table-column>
        <template #detail="props">
          <div class="message-detail">
            <div class="detail-section">
              <strong>Addressed to Filing Responsible:</strong>
              <div v-if="props.row.cc && props.row.cc.length">
                <strong>CC:</strong>
                <span :title="parseStringArray(props.row.cc).join(', ')">
                  {{ truncateText(parseStringArray(props.row.cc).join(", ")) }}
                </span>
              </div>
              <div v-if="props.row.bcc && props.row.bcc.length">
                <strong>BCC:</strong>
                <span class="email-text">
                  {{ parseStringArray(props.row.bcc).join("; ") }}
                </span>
              </div>
            </div>

            <div
              class="detail-section email-content"
              v-if="getEmailContent(props.row)"
              v-html="getEmailContent(props.row)"
            ></div>

            <div
              class="detail-section"
              v-if="parseAttachments(props.row.attachments).length > 0"
            >
              <table class="table is-narrow is-fullwidth">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Uploaded on</th>
                    <th>Uploaded by</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(attachment, index) in parseAttachments(
                      props.row.attachments
                    )"
                    :key="index"
                  >
                    <td>{{ attachment.document_name || "Attachment" }}</td>
                    <td>
                      {{ props.row.date }}
                      {{ formatTime(props.row.updated_at) }}
                    </td>
                    <td>{{ customerMap[props.row.customer] || "Unknown" }}</td>
                    <td>
                      <button
                        @click="downloadAttachment(attachment)"
                        class="button is-small"
                        title="Download"
                      >
                        <b-icon icon="download" size="is-small"></b-icon>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { forkJoin } from "rxjs";
import CommunicationAdd from "@/components/Customers/CommunicationAdd";
import CommunicationService from "@/services/CommunicationService";
import CustomerProfileDataService from "../../services/CustomerProfileDataService";
import utilityMixin from "@/mixins/UtilityMixin";
import ScopeMixin from "@/mixins/scopeMixin";
import { Permission } from "../../services/Permissions";
import { mapGetters } from "vuex";
// @group Customers
export default {
  name: "CommunicationListView",
  mixins: [utilityMixin, ScopeMixin],
  components: {
    CommunicationAdd
  },

  props: {
    checkable: {
      type: Boolean,
      default: false
    },
    contactAction: {
      type: Boolean,
      default: false
    },
    customerAccountManager: {
      type: Number,
      default: 0
    },
    backupCustomerAccountManager: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isModalActive: false,
      uploadCommunicationVisible: false,
      isAdd: false,
      isEdit: false,
      action: null,
      editObject: this.getClearFormObject(),
      tableHeight: "",
      users: [],
      checkedRow: [],
      data: [],
      filteredData: [],
      searchFilters: {
        date: "",
        type: "",
        title: "",
        receiver: ""
      },
      sortField: "date",
      sortOrder: "desc",

      customers: [],
      customerMap: {},
      containerMap: {},
      countryMap: {},
      schemeMap: {},
      columns: [
        {
          field: "date",
          label: "Date",
          width: "120",
          class: "enable-select-text"
        },
        {
          field: "type",
          label: "Type",
          width: "120",
          class: "enable-select-text"
        },
        {
          field: "title",
          label: "Title",
          class: "enable-select-text"
        },
        {
          field: "receiver",
          label: "Receiver",
          width: "200",
          class: "enable-select-text"
        },
        {
          field: "actions",
          label: "Actions",
          width: "20",
          class: "enable-select-text"
        }
      ],

      communicationService: new CommunicationService(),

      customerDataService: new CustomerProfileDataService()
    };
  },
  computed: {
    ...mapGetters(["userId", "userName", "userHasPermission"]),
    editObjectName() {
      return this.editObject || this.getClearFormObject();
    },
    filteredTableData() {
      const filtered = this.data.filter(item => {
        const dateMatch =
          !this.searchFilters.date ||
          (item.date &&
            item.date
              .toLowerCase()
              .includes(this.searchFilters.date.toLowerCase()));
        const typeMatch =
          !this.searchFilters.type ||
          (item.type &&
            item.type
              .toLowerCase()
              .includes(this.searchFilters.type.toLowerCase()));
        const titleMatch =
          !this.searchFilters.title ||
          (item.title &&
            item.title
              .toLowerCase()
              .includes(this.searchFilters.title.toLowerCase()));
        const receiverMatch =
          !this.searchFilters.receiver ||
          (item.receiver &&
            item.receiver
              .toLowerCase()
              .includes(this.searchFilters.receiver.toLowerCase()));

        return dateMatch && typeMatch && titleMatch && receiverMatch;
      });

      // Apply sorting
      if (this.sortField) {
        filtered.sort((a, b) => {
          let aVal = a[this.sortField];
          let bVal = b[this.sortField];

          // Handle null/undefined values
          if (aVal == null) aVal = "";
          if (bVal == null) bVal = "";

          // Convert to lowercase for string comparison
          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          // Compare values
          let comparison = 0;
          if (aVal < bVal) comparison = -1;
          if (aVal > bVal) comparison = 1;

          // Apply sort order
          return this.sortOrder === "asc" ? comparison : -comparison;
        });
      }

      return filtered;
    },
    countryList() {
      return this.$store.state.countryList;
    },
    schemeList() {
      return this.$store.state.schemeList;
    },
    containerList() {
      return this.$store.state.containerList;
    }
  },
  watch: {
    customerAccountManager() {
      //Watches value of CAM and triggers this function if value of CAM changes
      this.fetchCommunicationData();
    }
  },
  mounted() {
    this.fetchCountries();
    this.fetchSchemes();
    this.fetchContainers();
    forkJoin(this.fetchCustomerList()).subscribe(() => {
      this.fetchCommunicationData();
    });
  },

  methods: {
    checkCheckBox(d) {
      this.checkedRow = d;
    },

    getClearFormObject() {
      return {
        id: null,
        customer: Number(this.$route.params.id),
        country: null,
        scheme: null,
        stream: null,
        update_date: null,
        username: null,
        password: null,
        additional_info: null,
        website: null
      };
    },
    addModal() {
      this.isAdd = true;
      this.isEdit = false;
      this.action = "Add";
      this.editObject = this.getClearFormObject();
      this.isModalActive = true;
    },
    editModal(editObject) {
      this.isAdd = true;
      this.isEdit = true;
      this.action = "Update";
      this.fetchCommunicationSingleData(editObject);
      this.isModalActive = true;
    },
    viewModal(editObject) {
      this.isAdd = false;
      this.isEdit = false;
      this.action = "View";
      this.fetchCommunicationSingleData(editObject);
      this.isModalActive = true;
    },
    deleteModal(editObject) {
      this.isAdd = false;
      this.action = "Delete";
      this.fetchCommunicationSingleData(editObject);
      this.isModalActive = true;
    },
    editConfirm() {
      this.fetchCommunicationData();
      this.isModalActive = false;
      this.checkedRow = [];
      //this.$router.go();
    },
    editCancel() {
      this.isModalActive = false;
    },
    exportCommunication() {
      try {
        const set = new Set(
          this.checkedRow.map(cc => cc.id).map(result => JSON.stringify(result))
        );
        this.communicationService.export(
          Array.from(set).map(elem => JSON.parse(elem)),
          Number(this.$route.params.id)
        );
      } catch (e) {
        this.$buefy.dialog.alert({
          title: "Upload Error !!!",
          message: e.message,
          type: "is-danger",
          hasIcon: true,
          icon: "times-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      }
    },
    uploadCommunication() {
      this.uploadCommunicationVisible = true;
    },

    fetchCustomerList() {
      const customerObservable = this.customerDataService.getCustomerList(
        this.customerId
      );
      customerObservable.subscribe(
        customerProfiles => {
          this.customers = customerProfiles;
          this.customerMap = this.prepareMasterData(this.customers);
        },
        e => {
          console.log(e);
        }
      );
      return customerObservable;
    },
    prepareData(data) {
      for (const report in data) {
        // Format date from updated_at
        if (data[report]["updated_at"]) {
          const date = new Date(data[report]["updated_at"]);
          data[report]["date"] = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });
        }

        // Set type from notif_type
        data[report]["type"] = data[report]["notif_type"] || "Email";

        // Set title from subject
        data[report]["title"] = data[report]["subject"] || "";

        // Parse attachments if they're in string format
        if (
          data[report]["attachments"] &&
          typeof data[report]["attachments"] === "string"
        ) {
          try {
            data[report]["attachments"] = JSON.parse(
              data[report]["attachments"].replace(/'/g, '"')
            );
          } catch (e) {
            console.error("Error parsing attachments:", e);
          }
        }

        // Ensure attachments is always an array
        if (data[report]["attachments"]) {
          if (!Array.isArray(data[report]["attachments"])) {
            data[report]["attachments"] = [data[report]["attachments"]];
          }
        } else {
          data[report]["attachments"] = [];
        }

        // Parse CC and BCC if they're in string format
        if (data[report]["cc"] && typeof data[report]["cc"] === "string") {
          try {
            data[report]["cc"] = JSON.parse(
              data[report]["cc"].replace(/'/g, '"')
            );
          } catch (e) {
            console.error("Error parsing cc:", e);
          }
        }

        if (data[report]["bcc"] && typeof data[report]["bcc"] === "string") {
          try {
            data[report]["bcc"] = JSON.parse(
              data[report]["bcc"].replace(/'/g, '"')
            );
          } catch (e) {
            console.error("Error parsing bcc:", e);
          }
        }

        // Set receiver from cc or email
        let receiver = data[report]["cc"];
        if (Array.isArray(receiver)) {
          receiver = receiver.join(", ");
        }
        data[report]["receiver"] = receiver || data[report]["email"] || "";
      }
      this.data = data;

      if (this.data.length >= 10) {
        this.tableHeight = "height:560px";
      }
    },
    prepareMasterData(data) {
      const resultDict = {};
      for (const idx in data) {
        const dataDict = data[idx];
        resultDict[dataDict["id"]] = dataDict["name"];
      }
      return resultDict;
    },
    handleApiError(reason) {
      let errorMessage = null;
      if (reason?.response?.data) {
        if (reason.response.data["detail"]) {
          errorMessage = reason.response.data["detail"];
        } else if (reason.response.data["error"]) {
          errorMessage = reason.response.data["error"];
        } else if (reason.response.data["name"]) {
          errorMessage = reason.response.data["name"];
        }
      }

      if (errorMessage != null && errorMessage != undefined) {
        this.$buefy.dialog.alert({
          title: "Error",
          message: errorMessage,
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      }
    },
    fetchCommunicationSingleData(editObjectName) {
      this.communicationService
        .getAll(this.$route.params.id, editObjectName.id)
        .subscribe(response => {
          this.editObject = response;
        }, this.handleApiError);
    },
    isEditAllowed() {
      if (this.$route.fullPath.includes("/view/")) {
        this.showDelete = false;
        this.showEdit = false;
        return false;
      }
      if (
        this.userHasPermission(Permission.RTRACKER_REPORT_ASSIGN_ALL) ||
        (this.userHasPermission(Permission.CUSTOMER_PROFILE_CHANGE_CAM) &&
          (this.customerAccountManager == this.userId ||
            this.backupCustomerAccountManager == this.userId))
      ) {
        this.showDelete = true;
        this.showEdit = true;
        return true;
      } else {
        this.showDelete = false;
        this.showEdit = false;
        return false;
      }
    },
    fetchCommunicationData() {
      this.isLoading = true;
      this.communicationService
        .getAll(this.$route.params.id)
        .subscribe(response => {
          this.isLoading = false;
          this.prepareData(response);
        }, this.handleApiError);
    },
    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    downloadAttachment(attachment) {
      if (!attachment || !attachment.document) {
        this.$buefy.toast.open({
          message: "No document URL available",
          type: "is-danger"
        });
        return;
      }
      try {
        this.communicationService
          .downloadAttachment(attachment)
          .then(response => {
            // Extract filename from Content-Disposition header or use default
            let filename = `${attachment.document_name}.pdf`;
            const contentDisposition = response.headers["content-disposition"];
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(
                /filename="?([^"\n\r]+)"?/i
              );
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1];
              }
            } else if (attachment.document_name) {
              // Use attachment.document_name if available, ensure it has .pdf extension
              filename = attachment.document_name.includes(".pdf")
                ? attachment.document_name
                : `${attachment.document_name}.pdf`;
            }

            // response.data is already a Blob due to responseType: 'blob'
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(e => {
            this.$buefy.dialog.alert({
              title: "Download Error!",
              message: e.message || "Failed to download attachment",
              type: "is-danger",
              hasIcon: true,
              icon: "times-circle",
              iconPack: "fa",
              ariaRole: "alertdialog",
              ariaModal: true
            });
          });
      } catch (e) {
        this.$buefy.dialog.alert({
          title: "Download Error!",
          message:
            e.message || "An error occurred while downloading attachment",
          type: "is-danger",
          hasIcon: true,
          icon: "times-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      }
    },
    setSortField(field) {
      if (this.sortField === field) {
        // Toggle sort order if clicking the same field
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        // Set new sort field with ascending order
        this.sortField = field;
        this.sortOrder = "asc";
      }
    },
    getSortIcon(field) {
      if (this.sortField === field) {
        return this.sortOrder === "desc" ? "arrow-down" : "arrow-up";
      }
      return "arrow-up";
    },
    isSorted(field) {
      return this.sortField === field;
    },
    getNextSortOrder(field) {
      if (this.sortField !== field) return "asc";
      return this.sortOrder === "asc" ? "desc" : "asc";
    },
    truncateText(text, maxLength = 48) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    parseStringArray(value) {
      if (Array.isArray(value)) {
        return value;
      }
      if (typeof value === "string") {
        try {
          // Try to parse as JSON first
          const parsed = JSON.parse(value.replace(/'/g, '"'));
          return Array.isArray(parsed) ? parsed : [value];
        } catch {
          // If JSON parsing fails, try simple string parsing
          return value
            .split(/[;,]/)
            .map(item => item.trim())
            .filter(item => item);
        }
      }
      return [];
    },
    unescapeHtml(text) {
      const div = document.createElement("div");
      div.innerHTML = text;
      return div.textContent || div.innerText || text;
    },
    normalizeSerializedContent(text) {
      if (typeof text !== "string") return text;
      return text
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\'/g, "'")
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\");
    },
    extractHtmlPayload(content) {
      if (typeof content !== "string") return null;

      const alternativeMatch = content.match(
        /^Alternative\(content=(['"])([\s\S]*?)\1,\s*mimetype=(['"])text\/html\3\)$/
      );
      if (alternativeMatch && alternativeMatch[2]) {
        return alternativeMatch[2];
      }

      const tupleMatch = content.match(
        /^\(\s*(['"])([\s\S]*?)\1\s*,\s*(['"])text\/html\3\s*\)$/
      );
      if (tupleMatch && tupleMatch[2]) {
        return tupleMatch[2];
      }

      return null;
    },
    wrapUrlsInLinks(content) {
      if (!content || typeof content !== "string") return content;

      // Split by existing <a> tags so we don't process URLs inside them
      const parts = content.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/i);

      return parts
        .map((part, index) => {
          // Odd indices are <a> tags (from the split with capturing group)
          if (index % 2 === 1) {
            return part; // Don't process existing links
          }

          // Even indices are non-link content
          const urlRegex = /(https?:\/\/[^\s<>'"]+)/gi;
          return part.replace(urlRegex, url => {
            // Clean up trailing punctuation
            const cleanUrl = url.replace(/[.,;:!?)]$/, "");
            return `<a href="${cleanUrl}" target="_blank">${cleanUrl}</a>`;
          });
        })
        .join("");
    },
    getEmailContent(row) {
      const source = row?.content || row?.content_text;
      if (!source) return "";
      if (typeof source !== "string") return source;

      try {
        let content = source.trim();

        // Extract from wrapper formats (Alternative or tuple)
        const extracted = this.extractHtmlPayload(content);
        if (extracted) {
          content = extracted;
        }

        // Normalize escaped characters
        content = this.normalizeSerializedContent(content);

        // Unescape HTML entities to detect real tags
        const unescaped = content
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&amp;/g, "&");

        // Check if content contains any HTML tags
        const hasHtmlTags = /<\/?[a-z][\w-]*[^>]*>/i.test(unescaped);

        if (hasHtmlTags) {
          // If HTML tags are present (real or escaped), wrap URLs in links and return
          return this.wrapUrlsInLinks(unescaped);
        }

        // For plain text only: escape HTML special chars and convert newlines
        const escaped = content
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

        // Convert newlines and multiple spaces to HTML
        const result = escaped
          .replace(/\n\n+/g, "<br/><br/>")
          .replace(/\n/g, "<br/>")
          .replace(/ {2,}/g, match => "&nbsp;".repeat(match.length));

        // Wrap URLs in links
        return this.wrapUrlsInLinks(result);
      } catch {
        return String(source);
      }
    },
    parseAttachments(attachments) {
      if (!attachments) return [];
      if (Array.isArray(attachments)) {
        return attachments;
      }
      if (typeof attachments === "string") {
        try {
          // Try to parse as JSON first (convert single quotes to double quotes)
          const jsonStr = attachments.replace(/'/g, '"');
          return JSON.parse(jsonStr);
        } catch {
          // Return empty array if parsing fails
          return [];
        }
      }
      return [];
    }
  }
};
</script>

<style scoped>
.messages-container {
  padding: 1rem;
}

.message-detail {
  padding: 1rem;
  background-color: #fafafa;
  border-left: 3px solid #3273dc;
}

.detail-section {
  margin-bottom: 1rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section strong {
  display: block;
  margin-bottom: 0.5rem;
}

.email-content {
  line-height: 1.6;
  color: #363636;
}

.email-content blockquote {
  background-color: #f5f5f5;
  border-left: 4px solid #dbdbdb;
  padding: 1em;
  margin: 1em 0;
}

.email-content em {
  font-style: italic;
}

.email-content strong {
  font-weight: bold;
  display: inline;
  margin-bottom: 0;
}

.table.is-narrow td,
.table.is-narrow th {
  padding: 0.5em 0.75em;
}

.table thead th {
  vertical-align: middle;
  padding: 0.5rem !important;
}

.field {
  margin-bottom: 0 !important;
}

.control input {
  font-size: 0.875rem;
}

::v-deep .table thead tr:first-child th {
  border-bottom: 2px solid #dbdbdb;
}

::v-deep .b-table .table thead th {
  background-color: #fafafa;
}

.header-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-cell span {
  font-weight: 600;
  color: #363636;
}

.sort-link {
  color: #3273dc;
  cursor: pointer;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.sort-link:hover {
  color: #2366d1;
  text-decoration: underline;
}

.sort-link.active {
  color: #2366d1;
  font-weight: bold;
}

::v-deep .b-table .table th {
  padding: 0.5rem !important;
}

::v-deep .b-table .table td {
  vertical-align: middle;
}

::v-deep .b-table-column[data-field="receiver"] {
  overflow: visible;
}

::v-deep .b-table-column[data-field="receiver"] .field {
  overflow: visible;
}

.message-detail {
  overflow-x: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.detail-section {
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.detail-section > div {
  margin-bottom: 0.75rem;
}

.detail-section strong {
  display: inline;
  margin-right: 0.5rem;
}

.detail-section span {
  display: inline;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: normal;
  white-space: normal;
}

.email-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 0.5rem;
}

.email-item {
  padding: 0.25rem 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
}

.email-text {
  display: inline-block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: normal;
  white-space: normal;
}
</style>
